import React, { useCallback, useEffect, useRef, useState } from "react";

import { Grid, Tooltip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import ShipeeziNewGrid, {
  Column,
  Option,
  RowActionsExtra,
} from "Components/AppGrid/shipeezi-new-grid";
import { colors } from "@zapalacorp/tokens";
import classesnames from "classnames";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { BiMove } from "react-icons/bi";
import { useHistory } from "react-router-dom";

import { Color } from "../../../../../components/Form/Button";
import BadgeStorageBoxStatus, {
  StorageStatuses,
} from "../../../../../components/Generic/BadgeStorageBoxStatus";
import History, { Item } from "../../../../../components/Generic/History";
import IconActivity from "../../../../../components/Generic/Icons/IconActivity";
import SkeletonDetails from "../../../../../components/Generic/LoadingSkeleton";
import SubHeader from "../../../../../components/Generic/SubHeader";
import SideViewComponent, {
  Navigation,
  PropHandles,
} from "../../../../../components/Generic/ViewJobComponent";
import {
  MoreOptions,
  MoreOptionsComponentHandles,
} from "../../../../../components/MoreOptionsComponent/MoreOptions";
import { useAppState } from "../../../../../context/StateContext";
import { Job } from "../../../../../models/job";
import { ItemJob } from "../../../../../models/sales";
import { Filters } from "../../../../../models/shipeezi.table";
import { StorageBox, StorageStatus, StorageBoxTypeOption } from "../../../../../models/storage.box";
import { StorageBoxHistory } from "../../../../../models/storage.box.histories";
import { StorageBoxType } from "../../../../../models/storage.box.type";
import StorageBoxHistoriesService from "../../../../../services/storage.box.history.service";
import StorageBoxTypeService from "../../../../../services/storage.box.type.service";
import { handleGetConvertValue } from "../../../../../util/converts";
import { ActivitiesTypeValues } from "../../../../../util/enums";
import { getCurrentLocationStorageBox } from "../../../../../util/getCurrentLocationStorageBox";
import { getMeasurement } from "../../../../../util/getMeasurement";
import { groupStorageBoxOrders } from "../../../../../util/groupStorageBoxOrders";
import {
  OrderView,
  OrderViewComponentHandler,
} from "../Transshipments/components/OrderViewComponent";
import StorageBoxDetails from "./components/Details";
import { UnloadOrdersModal } from "./components/UnloadOrdersModal";
import { useActions } from "./hooks/useActions";
import useStyles, {
  ChangeLocationIcon,
  OrderOption,
  StorageBoxesAndUnitsGrid,
  UnloadIcon,
  ViewIcon,
} from "./styles";
import { checkResourceCan, FN_NAMES, hasPermission, isShowAction } from "Util/permissions";
import { ResourceCanEnum, ResourceName, ResourceObjectTypeEnum } from "Util/enums/accessRule";
import { formatDateWithUserPref } from "Util/dateFormaterPreference";

export const StorageBoxesAndUnitsPage = () => {
  const { t: translate } = useTranslation();
  const sideViewComponentRef = useRef<PropHandles>(null);
  const history = useHistory();
  const {
    getById,
    page,
    total,
    pageSize,
    setPageSize,
    setSorting,
    hasError,
    handleSearch,
    handleFilter,
    handleSorting,
    handleCurrentPage,
    useUnloadStorageBox,
    storageBoxesAndUnitsData,
    getStorageBoxesAndUnits,
    verifyIsGetByJobOrCustomer,
  } = useActions();

  const classes = useStyles();

  const { currentUser, userPermissions } = useAppState();

  const [storageBoxId, setStorageBoxId] = useState<number>();
  const [openUnloadStorageBoxAndUnitModal, setOpenUnloadStorageBoxAndUnitModal] =
    useState<boolean>(false);
  const [ordersToUnload, setOrdersToUnload] = useState<Job[]>([]);
  const [selectedStorageBox, setSelectedStorageBox] = useState<StorageBox>();
  const [storageBoxTypesList, setStorageBoxTypesList] = useState<StorageBoxTypeOption[]>([]);
  const [isUnloaded, setIsUnloaded] = useState<boolean>(false);

  const [disableSortingType, setDisableSortingType] = useState<boolean>(false);
  const [disableSortingStatus, setDisableSortingStatus] = useState<boolean>(false);

  const ordersNumberMenu = useRef<MoreOptionsComponentHandles>(null);
  const orderViewComponentRef = useRef<OrderViewComponentHandler>(null);
  const [warehousesToView, setWarehousesToView] = useState<any[]>([]);
  const [storageBoxesUnitPermission, setStorageBoxesUnitPermission] = useState<{
    view: boolean;
    unload: boolean;
    changeLocation: boolean;
  }>({
    view: false,
    unload: false,
    changeLocation: false,
  });

  const { isFetching } = getStorageBoxesAndUnits();

  const { mutate: unloadStorageBox, isLoading: isUnloading, isSuccess } = useUnloadStorageBox();
  const { refetch, data: response, isFetching: fetchingStorageBox } = getById(Number(storageBoxId));

  const transformDataToOptions = (data: StorageBoxType[]): StorageBoxTypeOption[] => {
    return data.map(({ id, name }) => ({
      value: id?.toString() ?? "0",
      label: name,
    }));
  };

  useEffect(() => {
    if (userPermissions && userPermissions?.length > 0) {
      setStorageBoxesUnitPermission({
        view: hasPermission(userPermissions, FN_NAMES.FUNCTION_WAREHOUSING_STORAGE_VIEW),
        unload:
          checkResourceCan(
            userPermissions,
            ResourceName.STORAGE_BOX_AND_UNIT,
            ResourceCanEnum.EDIT
          ) && hasPermission(userPermissions, FN_NAMES.FUNCTION_WAREHOUSING_STORAGE_UNLOAD),
        changeLocation:
          checkResourceCan(
            userPermissions,
            ResourceName.STORAGE_BOX_AND_UNIT,
            ResourceCanEnum.EDIT
          ) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_WAREHOUSING_STORAGE_CHANGE_LOCATION),
      });
      const storageBoxResourceId = userPermissions?.find((permission) => {
        return (
          permission?.objectType === ResourceObjectTypeEnum.RESOURCE &&
          permission?.objectDetails?.name === ResourceName.STORAGE_BOX_AND_UNIT
        );
      })?.objectDetails?.id;

      const warehouses = userPermissions
        ?.map((permission) => {
          if (
            permission?.objectType === ResourceObjectTypeEnum.FUNCTION &&
            permission?.objectDetails?.objectData === "warehouse" &&
            permission?.resourceId === storageBoxResourceId
          ) {
            return permission;
          }
        })
        ?.filter((permission) => !!permission);

      setWarehousesToView(warehouses);
    }
  }, [userPermissions]);

  useQuery(
    ["storage.box.types.list"],
    () =>
      StorageBoxTypeService.getAll({
        isActive: "1",
        systemDefault: "0",
        companyId: currentUser?.companyId,
        sortBy: JSON.stringify([["name", "asc"]]),
      }),
    {
      refetchOnWindowFocus: false,
      enabled: !!currentUser?.companyId,
      onSuccess: ({ data }) => {
        if (data) {
          setStorageBoxTypesList(transformDataToOptions(data));
        }
      },
    }
  );

  const storageStatusOptions: Option[] = [
    { value: StorageStatuses.EMPTY, label: translate("empty") },
    { value: StorageStatuses.FULL, label: translate("full") },
    { value: StorageStatuses.IN_TRANSIT, label: translate("inTransit") },
    { value: StorageStatuses.LOADED, label: translate("loaded") },
    { value: StorageStatuses.INACTIVE, label: translate("inactive") },
    { value: StorageStatuses.UNUSABLE, label: translate("unusable") },
  ];

  const enableOrDisableSortingFilter = (filters: Filters) => {
    setDisableSortingStatus(false);
    setDisableSortingType(false);

    const propertyNames = Object.keys(filters);

    if (propertyNames?.[0]?.includes("storageStatus")) {
      setDisableSortingStatus(true);
    }
    if (propertyNames?.[0]?.includes("storageBoxType")) {
      setDisableSortingType(true);
    }
  };

  const filterTable = (filters: Filters) => {
    setSorting(undefined);
    handleFilter(filters);
    enableOrDisableSortingFilter(filters);
  };

  const getTotalItemsVolume = (items?: ItemJob[]) => {
    if (items && items.length > 0) {
      const itemsTotalVolume = items
        .map((item) => item.volume || 0)
        .reduce(
          (total, nextVolume) =>
            total ? total + Number(nextVolume || 0) : Number(nextVolume || 0),
          0
        );

      return itemsTotalVolume;
    }

    return 0;
  };

  const getStorageBoxOrdersReferences = (storageBox: StorageBox) => {
    let jobs =
      storageBox.itemsJobs?.reduce((jobs: Job[], itemJob: ItemJob) => {
        if (itemJob.job) jobs.push(itemJob.job);

        return jobs;
      }, []) || [];

    if (storageBox.jobs) {
      jobs = [...jobs, ...storageBox.jobs];
    }

    const orders = groupStorageBoxOrders(jobs);

    orders?.sort((orderA, orderB) => {
      if (orderA?.reference && orderB?.reference) {
        if (orderA?.reference?.toLowerCase() > orderB?.reference?.toLowerCase()) {
          return 1;
        }
        if (orderA?.reference?.toLowerCase() < orderB?.reference?.toLowerCase()) {
          return -1;
        }
      }

      return 0;
    });

    return (
      <MoreOptions
        ref={ordersNumberMenu}
        title={translate("moreOrders")}
        manyOptionsToShow={2}
        singleOptionPerLine={true}
        data={orders}
        returnLabel={(job: Job) =>
          `${job?.reference}${
            job.sequence && Number(job.sequence) > 0 ? `/${job.sequence}` : ""
          } - ${job?.customer?.name ?? ""}`
        }
        asComponent={(label: string, job: Job) => (
          <OrderOption
            onClick={() => {
              ordersNumberMenu?.current?.setAnchor(null);
              orderViewComponentRef?.current?.open(Number(job.id));
            }}
          >
            {label}
          </OrderOption>
        )}
      />
    );
  };

  const getStorageBoxOrders = (storageBox?: StorageBox): Job[] => {
    if (!storageBox) return [];

    return [
      ...(storageBox?.itemsJobs
        ?.filter((item) => item.job !== undefined)
        ?.map((item) => item.job) || []),
      ...(storageBox?.jobs || []),
    ] as Job[];
  };

  const canUnloadStorageBox = (storageBox?: StorageBox): boolean => {
    if (!storageBox) return false;

    return (
      (storageBox?.itemsJobs && storageBox?.itemsJobs && storageBox?.itemsJobs?.length > 0) ||
      (storageBox?.jobs && storageBox?.jobs && storageBox?.jobs?.length > 0) ||
      false
    );
  };

  const columns: Column[] = [
    { name: "number", title: translate("number"), hideSort: verifyIsGetByJobOrCustomer() },
    {
      name: "storageBoxType",
      searchIdentifier: "storageBoxType",
      title: translate("type"),
      hideSort: disableSortingType || verifyIsGetByJobOrCustomer(),
      formatComponent: (storageBoxType: StorageBoxType) =>
        storageBoxType && storageBoxType?.name && storageBoxType.name,
    },
    {
      name: "itemsJobs",
      placeholderSearch: translate("orderOrCustomer"),
      title: translate("order"),
      hideSort: verifyIsGetByJobOrCustomer(),
      searchIdentifier: "jobOrCustomer",
      formatComponent: (_itemsJobs: ItemJob[], storageBox: StorageBox) =>
        getStorageBoxOrdersReferences(storageBox),
    },
    {
      name: "mapCells",
      title: translate("currentLocation"),
      hideSearch: true,
      hideSort: true,
      formatComponent: (_, storageBox: StorageBox) =>
        getCurrentLocationStorageBox(storageBox, translate),
    },
    {
      name: "storageBoxType",
      title: translate("capacity"),
      hideSort: true,
      hideSearch: true,
      formatText: (storageBoxType: StorageBoxType) => {
        return `${handleGetConvertValue(
          storageBoxType.capacity || 0,
          getMeasurement()?.code,
          getMeasurement()?.decimalPlaces
        )} ${getMeasurement()?.code}`;
      },
    },
    {
      name: "itemsJobs",
      title: translate("inUse"),
      hideSearch: true,
      hideSort: true,
      formatText: (items: ItemJob[]) => {
        if (items && items.length > 0) {
          const totalItemsVolume = getTotalItemsVolume(items);

          return `${handleGetConvertValue(
            totalItemsVolume || 0,
            getMeasurement()?.code,
            getMeasurement()?.decimalPlaces
          )} ${getMeasurement()?.code}`;
        }

        return `${handleGetConvertValue(
          0,
          getMeasurement()?.code,
          getMeasurement()?.decimalPlaces
        )} ${getMeasurement()?.code}`;
      },
    },
    {
      name: "updatedAt",
      title: translate("updated"),
      hideSearch: true,
      formatComponent: (updatedAt?: Date) =>
        updatedAt &&
        currentUser?.preferences && (
          <Grid item>
            <div>
              <span>{formatDateWithUserPref(updatedAt, currentUser.preferences, "dateOnly")}</span>
            </div>
            <div>
              <span>{formatDateWithUserPref(updatedAt, currentUser.preferences, "timeOnly")}</span>
            </div>
          </Grid>
        ),
    },
    {
      name: "storageStatus",
      searchIdentifier: "storageStatus",
      hideSearch: true,
      hideSort: disableSortingStatus || verifyIsGetByJobOrCustomer(),
      title: translate("status"),
      formatComponent: (storageStatus: StorageStatus) => (
        <BadgeStorageBoxStatus value={storageStatus.name} />
      ),
    },
  ];

  const viewStorageBoxAndUnit = (index: number) => {
    const storageBoxAndUnitToView = storageBoxesAndUnitsData.find(
      (storageBoxAndUnit) => Number(storageBoxAndUnit.id) === Number(index)
    );

    return (
      <Tooltip title={translate("view")} placement="top">
        <div>
          <ViewIcon
            size={18}
            color="#475467"
            onClick={() => {
              setStorageBoxId(storageBoxAndUnitToView?.id);
              sideViewComponentRef.current?.setOpenSideBar(true);
            }}
          />
        </div>
      </Tooltip>
    );
  };

  const unloadStorageBoxAndUnit = (storageBoxId: number) => {
    const storageBoxAndUnitToUnload = storageBoxesAndUnitsData.find(
      (storageBoxAndUnit) => Number(storageBoxAndUnit.id) === Number(storageBoxId)
    );

    if (canUnloadStorageBox(storageBoxAndUnitToUnload)) {
      return (
        <Tooltip title={translate("unload")} placement="top">
          <div>
            <UnloadIcon
              size={18}
              color="#475467"
              onClick={() => {
                if (canUnloadStorageBox(storageBoxAndUnitToUnload)) {
                  setSelectedStorageBox(storageBoxAndUnitToUnload);
                  setOrdersToUnload(
                    groupStorageBoxOrders(getStorageBoxOrders(storageBoxAndUnitToUnload))
                  );
                  setOpenUnloadStorageBoxAndUnitModal(true);
                }
              }}
            />
          </div>
        </Tooltip>
      );
    }

    return null;
  };

  const changeLocationStorageBoxAndUnit = (storageBoxId: number) => {
    const storageBoxAndUnitToChangeLocation = storageBoxesAndUnitsData.find(
      (storageBoxAndUnit) => Number(storageBoxAndUnit.id) === Number(storageBoxId)
    );

    if (storageBoxAndUnitToChangeLocation?.id) {
      return (
        <Tooltip title={translate("changeLocation")} placement="top">
          <div>
            <ChangeLocationIcon
              size={18}
              color="#475467"
              onClick={() => {
                history.push("/app/movement-mnt", {
                  type: "storageBox",
                  storageBoxId: storageBoxAndUnitToChangeLocation?.id,
                });
              }}
            />
          </div>
        </Tooltip>
      );
    }

    return null;
  };

  const setSideBarViewTitle = (): string => {
    let title = translate("storageBox");
    const storageBoxNumber = response?.data?.number;

    if (storageBoxNumber) title = `${title} "${storageBoxNumber}"`;

    return title;
  };

  const getStorageBoxHistory = useCallback(
    (storageBoxHistories: StorageBoxHistory[]) => {
      if (storageBoxHistories && storageBoxHistories?.length) {
        return storageBoxHistories?.map((history) => {
          let date;

          if (history?.createdAt) {
            date = format(new Date(history?.createdAt), "dd/MM/yyyy p");
          }

          return {
            title: history?.description,
            activityDate: date ?? "",
            activityUser: `${history?.user?.name} ${history?.user?.lastName}`,
            activityUserIp: history?.userIpAddress,
          } as Item;
        });
      }

      return [] as Item[];
    },
    [response?.data]
  );

  const setNavigation = useCallback(() => {
    return [{ key: Navigation.DETAILS }];
  }, [response?.data]);

  const setTabs = useCallback(() => {
    return [
      {
        key: Navigation.DETAILS,
        loading: fetchingStorageBox,
        isEmpty: !response?.data,
        isOpened: false,
        renderLoading: () => <SkeletonDetails />,
        render: () => <StorageBoxDetails data={response?.data ?? ({} as StorageBox)} />,
      },
    ];
  }, [response?.data, fetchingStorageBox]);

  const historyFilterTypeOptions: Option[] = [
    { value: "", label: translate("allTypes") },
    { value: ActivitiesTypeValues.EDITED, label: translate("editions") },
    { value: ActivitiesTypeValues.MOVEMENT, label: translate("movements") },
    { value: ActivitiesTypeValues.LOAD, label: translate("loads") },
    { value: ActivitiesTypeValues.UNLOAD, label: translate("unloads") },
  ];

  const setExtraButtons = useCallback(
    () => [
      // TODO: uncomment button when View in Warehouse map gets ready
      // {
      //   buttonLabel: translate("viewInWarehouseMap"),
      //   class: classesnames(classes.p0, classes.buttonDisabled),
      //   classLabel: classes.labelDisabled,
      //   color: Color.DEFAULT,
      //   icon: <BoxIcon />,
      //   tooltip: translate("funcionalityUnderDevelopment"),
      // },
      {
        buttonLabel: translate("unload"),
        class: classesnames(classes.p0, classes.button),
        classLabel: classes.label,
        color: Color.DEFAULT,
        disabled: !response?.data.itemsJobs?.length,
        icon: <UnloadIcon size={20} color="#308CDB" />,
        action: (): void => {
          if (response?.data && response.data.itemsJobs?.length) {
            setSelectedStorageBox(response?.data);
            setOrdersToUnload(groupStorageBoxOrders(getStorageBoxOrders(response?.data)));
            setOpenUnloadStorageBoxAndUnitModal(true);
          }
        },
      },
      {
        buttonLabel: translate("changeLocation"),
        class: classesnames(classes.p0, classes.button),
        classLabel: classes.label,
        color: Color.DEFAULT,
        icon: <BiMove size={20} color="#308CDB" />,
        action: () => {
          history.push("/app/movement-mnt", {
            type: "storageBox",
            storageBoxId: response?.data.id,
          });
        },
      },
    ],
    [response?.data]
  );

  const setActivityButton = useCallback(() => {
    return {
      buttonLabel: translate("viewActivity"),
      titlePage: translate("activity"),
      class: classesnames(classes.p0, classes.activityButton),
      color: Color.DEFAULT,
      icon: <IconActivity width={20} height={20} color={colors.brandBlue500} />,
      renderComponent: () => (
        <History
          getHistoryItems={getStorageBoxHistory}
          historyFilterTypeOptions={historyFilterTypeOptions}
          countActivitiesRequest={{
            request: StorageBoxHistoriesService.count,
            requestArgs: { storageBoxId: response?.data.id },
          }}
          getActivitiesRequest={{
            request: StorageBoxHistoriesService.getAll,
            requestArgs: {
              storageBoxId: response?.data.id,
              pageSize: 25,
              sortBy: JSON.stringify([["createdAt", "DESC"]]),
              include: JSON.stringify([
                { association: "user", attributes: ["id", "name", "lastName"] },
              ]),
            },
          }}
        />
      ),
    };
  }, [response?.data]);

  useEffect(() => {
    if (isSuccess && isUnloaded && sideViewComponentRef.current?.isOpen) {
      refetch();
      setIsUnloaded(false);
    }
  }, [isSuccess, isUnloaded]);

  const handleRowActionsExtraByPermissions = () => {
    let actions: RowActionsExtra[] = [];

    if (isShowAction(currentUser, storageBoxesUnitPermission.view)) {
      actions?.push({
        title: translate("view"),
        key: "handleView",
        buttonAndAction: viewStorageBoxAndUnit,
      });
    }

    if (isShowAction(currentUser, storageBoxesUnitPermission.unload)) {
      actions?.push({
        title: translate("unload"),
        key: "handleUnload",
        buttonAndAction: unloadStorageBoxAndUnit,
      });
    }

    if (isShowAction(currentUser, storageBoxesUnitPermission.changeLocation)) {
      actions?.push({
        title: translate("changeLocation"),
        key: "handleChangeLocation",
        buttonAndAction: changeLocationStorageBoxAndUnit,
      });
    }

    return actions;
  };

  return (
    <div data-cy="storageBoxesAndUnitsGrid">
      {/* <SubHeader title={translate("storageBoxesAndUnits")} showWarehouseSelect warehousesToView={warehousesToView} /> */}
      <SubHeader showWarehouseSelect warehousesToView={warehousesToView} />
      <StorageBoxesAndUnitsGrid>
        <ShipeeziNewGrid
          data={storageBoxesAndUnitsData}
          columns={columns}
          totalItems={total}
          loadingData={isFetching}
          emptyData={translate("noInformationToBeShown")}
          pageSizeList={[15, 40, 60, 100]}
          pageSize={pageSize}
          onChangePageSize={setPageSize}
          currentPage={page}
          onNextPage={handleCurrentPage}
          onPreviousPage={handleCurrentPage}
          onGoToPage={handleCurrentPage}
          onSorting={handleSorting}
          generalActions={{
            orderBy: {
              left: ["handleSearch", "handleFilterBy"],
            },
            handleSearch: {
              onSearch: handleSearch,
            },
            handleFilterBy: {
              categories: [
                {
                  name: translate("status"),
                  options: storageStatusOptions,
                  column: "storageStatus.name",
                },
                {
                  name: translate("type"),
                  options: storageBoxTypesList,
                  column: "storageBoxType.name",
                },
              ],
              onChange: (filters) => filterTable(filters),
            },
          }}
          rowActions={{
            orderBy: ["handleView", "handleUnload", "handleChangeLocation"],
          }}
          rowActionsExtra={handleRowActionsExtraByPermissions()}
          positionActionsColumn="right"
        />
        {openUnloadStorageBoxAndUnitModal && (
          <UnloadOrdersModal
            open={openUnloadStorageBoxAndUnitModal}
            closeModal={() => setOpenUnloadStorageBoxAndUnitModal(false)}
            hasError={hasError}
            isLoading={isUnloading}
            storageBoxAndUnitOrders={ordersToUnload}
            unloadStorageBox={(selectedOrders: Job[]) => {
              if (selectedStorageBox) {
                unloadStorageBox({
                  storageBox: { id: selectedStorageBox?.id } as StorageBox,
                  orders: selectedOrders,
                });
                setIsUnloaded(true);
              }
            }}
          />
        )}
        <OrderView ref={orderViewComponentRef} />
      </StorageBoxesAndUnitsGrid>

      <SideViewComponent
        ref={sideViewComponentRef}
        isLoading={fetchingStorageBox}
        title={setSideBarViewTitle()}
        navigation={setNavigation()}
        tabs={setTabs()}
        onClose={() => {
          setStorageBoxId(undefined);
        }}
        buttonsComponent={setExtraButtons()}
        activityButton={setActivityButton()}
      />
    </div>
  );
};
